/**
* @summary - Component that Provides Attachments Tab
* @author Yuvasri S
* @since 2024-07-04
*/
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { randomId } from '@mui/x-data-grid-generator';
import {
    Box,
    Button,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import TabContentLayout from './TabContentLayout';
import { DefaultCard } from '../../assets/styled-components/card';
import CustomTextField from '../commoncomponents/CustomTextField';
import { getAuthToken } from "../../utility/tokenUtility";
import { MasterGraph } from '../../utility/apiURLS';
import CustomDialog from '../commoncomponents/CustomDialog';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import { v4 as uuidv4 } from 'uuid';
import SectionTitle from '../commoncomponents/SectionTitle';
import AddRoundedIcon from "@mui/icons-material/AddRounded";
const initialRow = {
    id: 1,
    file: null,
    documenttitle: '',
    documenttype: '',
    filesize: '',
};


export default function AttachmentsContent({ hospitalcode, hospitalname, hospitalid, isViewMode, isEditMode }) {
    const [rows, setRows] = useState(isViewMode ? [] : [initialRow]);

    //  const [rows, setRows] = useState([initialRow]);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const navigate = useNavigate();
    const hospitalDetailsId = useSelector((state) => state.form.mashospitaldetailsId);
    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const facilityId = hospitalid;
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [registeredFiles, setRegisteredFiles] = useState([]);
    const [storeFileName, setStoreFileName] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [deletedFilesArray, setDeletedFilesArray] = useState([]);  // New state to track deleted files
    const [deletedRowIds, setDeletedRowIds] = useState([])
    const [previouslyUploadedFiles, setPreviouslyUploadedFiles] = useState([]);  // Track already uploaded files

    const fetchFiles = async () => {
        try {
            const response = await axios.get(`${MasterGraph}/api/GraphHospitalMaster/GetHospitalAttachmentList/${hospitalDetailsId}`, config);
            const fetchedFiles = response.data; // Assuming the response is an array of files
            // setRows(isViewMode ? fetchedFiles : (fetchedFiles.length > 0 ? fetchedFiles : [initialRow]));

            if (response?.data?.length > 0) {
                setRows(fetchedFiles);
            } else if (isViewMode == true) {
                setRows(fetchedFiles);
            }
            else {
                const newRow = {
                    id: 1,
                    category: '',
                    fileType: '',
                    designation: '',
                    size: '',
                };
                setRows([newRow]);
            }

        } catch (error) {
            console.error('Error fetching files:', error);
        }
    };

    useEffect(() => {

        if (hospitalcode) {
            fetchFiles();
        }
    }, [hospitalcode]);

    const handleDeleteClick = (id) => () => {
        // Find the row to delete by its ID
        const rowToDelete = rows.find(row => row.id === id);

        // Check if either file is null or category is an empty string
        if (rowToDelete && (rowToDelete.file === null || rowToDelete.category === "")) {
            // If either condition is met, delete immediately without confirmation
            const updatedRows = rows.filter((row) => row.id !== id);
            setRows(updatedRows);

            // Move the deleted file to `deletedFilesArray` if applicable
            const fileToDelete = selectedFiles.find((file) => file.id === id);
            if (fileToDelete) {
                setDeletedFilesArray((prevDeletedFiles) => [...prevDeletedFiles, fileToDelete]);  // Add to deleted files array
            }

            // Remove the deleted file from `selectedFiles`
            const updatedSelectedFiles = selectedFiles.filter((file) => file.id !== id);
            setSelectedFiles(updatedSelectedFiles);  // Update selectedFiles array

            // Remove the file from `previouslyUploadedFiles` if it was already uploaded
            const updatedPreviouslyUploadedFiles = previouslyUploadedFiles.filter(file => file.id !== id);
            setPreviouslyUploadedFiles(updatedPreviouslyUploadedFiles);

            // If no rows are left, add a default empty row
            if (updatedRows.length === 0) {
                setRows([initialRow]);  // Add default empty row if no rows are left
            }
        } else {
            // If neither condition is met, open the delete confirmation dialog
            setSelectedId(id);
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = () => {
        const id = selectedId; // Get the selected row ID

        // Move the row to the deleted rows array (only if it's a valid database ID)
        if (id && typeof id === 'string') {
            setDeletedRowIds((prevDeletedRowIds) => [...prevDeletedRowIds, id]);
        }

        // Remove the row from the table
        const updatedRows = rows.filter((row) => row.id !== id);
        setRows(updatedRows);

        // Move the deleted file to `deletedFilesArray`
        const fileToDelete = selectedFiles.find((file) => file.id === id);
        if (fileToDelete) {
            setDeletedFilesArray((prevDeletedFiles) => [...prevDeletedFiles, fileToDelete]);  // Add to deleted files array
        }

        // Remove the deleted file from `selectedFiles`
        const updatedSelectedFiles = selectedFiles.filter((file) => file.id !== id);
        setSelectedFiles(updatedSelectedFiles);  // Update selectedFiles array

        // Remove the file from `previouslyUploadedFiles` if it was already uploaded
        const updatedPreviouslyUploadedFiles = previouslyUploadedFiles.filter(file => file.id !== id);
        setPreviouslyUploadedFiles(updatedPreviouslyUploadedFiles);

        // If no rows are left, add a default empty row
        if (updatedRows.length === 0) {
            setRows([initialRow]);  // Add default empty row if no rows are left
        }

        setDeleteDialogOpen(false);  // Close the delete confirmation dialog
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialogOpen(false);
    };

    const handleAddRow = () => {
        const id =
            rows.length > 0
                ? Math.max(...rows.map((row) => (isNaN(row.id) ? 0 : Number(row.id)))) + 1
                : 1;
        const newRow = {
            id: id,
            file: null,
            documenttitle: '',
            documenttype: '',
            filesize: '',
        };
        setRows([...rows, newRow]);
    };

    const handleFileChange = (event, id) => {
        const file = event.target.files[0];
        if (!file) return;
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop().toLowerCase();
        if (file.size > 20 * 1024 * 1024) {
            setError(true);
            setErrorMessage('Error: File size exceeds 20 MB. Please select smaller files.');
            return;
        }

        const allowedFileExtensions = ['doc', 'jpeg', 'jpg', 'png', 'xlsx', 'csv', 'pdf', 'docx'];
        if (!allowedFileExtensions.includes(fileExtension)) {
            setError(true);
            setErrorMessage('Error: Only DOC, JPEG, JPG, PNG, XLSX, CSV, and PDF files are allowed.');
            return;
        }

        const fileNameWithoutExtension = fileName.replace(new RegExp(`.${fileExtension}$`), '');

        let updatedSelectedFiles = [...selectedFiles];
        const fileIndex = updatedSelectedFiles.findIndex((file) => file.id === id);
        if (fileIndex > -1) {
            updatedSelectedFiles[fileIndex] = { id, file };
        } else {
            updatedSelectedFiles.push({ id, file });
        }
        setSelectedFiles(updatedSelectedFiles);

        const updatedRows = rows.map((row) => {
            if (row.id === id) {
                let fileSize;
                if (file.size >= 1024 * 1024) {
                    fileSize = (file.size / (1024 * 1024)).toFixed(2) + ' MB';
                } else {
                    fileSize = (file.size / 1024).toFixed(2) + ' KB';
                }

                return {
                    ...row,
                    file,
                    documenttitle: fileName,
                    documenttype: fileExtension,
                    filesize: fileSize,
                };
            }
            return row;
        });

        setRows(updatedRows);
        setHasUnsavedChanges(true);

        // Debug: log the updated rows state
    };



    const handleDownload = async (file) => {
        try {
            // Perform the GET request to download the file
            const response = await axios.get(`${MasterGraph}/api/FileUpload/DownloadFile/${file.filename}`, {
                ...config,
                responseType: 'blob'
            });

            if (response) {
                const url = URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.documenttitle || file.filename; // Use documenttitle as the filename
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
            else {
                if (file && file instanceof Blob) {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.documenttitle || file.filename; // Use documenttitle as the filename
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                } else {
                    console.error('Invalid file object:', file);
                }
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };



    const handleDownloadClick = (event, file) => {
        event.preventDefault();
        handleDownload(file);
    };
    function uuidv4(extension) {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        }) + '.' + extension;
    }

    const uploadFiles = async (formData) => {
        try {
            let storeFileNameArray = [];
            let storeregsitredFilesResponse = [];
            let storefileUploadResponse = [];
            for (let i = 0; i < selectedFiles?.length; i++) {
                const formData = new FormData();
                const extension = selectedFiles[i]?.file?.name.split('.').pop();
                const fileName = uuidv4(extension); // generate a unique id
                formData.append('fileObj', selectedFiles[i]?.file, fileName); // append file with unique id as filename
                storeFileNameArray.push({ id: fileName, filename: `${selectedFiles[i]?.file?.name}`, selectedFileId: selectedFiles[i]?.id });

                try {
                    const fileUploadResponse = await axios.post(`${MasterGraph}/api/fileupload`, formData, config);
                    storeregsitredFilesResponse.push(fileUploadResponse.data);
                    storefileUploadResponse.push(fileUploadResponse.data);
                } catch (error) {
                    console.log('Error uploading file:', error.response ? error.response.data : error.message);
                }
            }
            setStoreFileName(storeFileNameArray);
            setRegisteredFiles(storeregsitredFilesResponse);
            return { storefileUploadResponse, storeFileNameArray };
        } catch (error) {
            console.error('Save error:', error);
            throw error;
        }
    };

    const saveFilesToAttachment = async (payload) => {
        try {
            const response = await axios.post(`${MasterGraph}/api/mashospitalattachments`, payload, config);
            if (response.status >= 200 && response.status <= 204) {
                setOpenDialog(true);
            }
            return response.data;
        } catch (error) {
            console.error('Save error:', error);
            throw error;
        }
    };

    const handleSaveClick = async () => {
        try {
            // Filter selectedFiles to ensure we only upload files still linked to existing rows and not yet uploaded
            const filteredSelectedFiles = selectedFiles
                .filter(file => rows.some(row => row.id === file.id))
                .filter(file => !previouslyUploadedFiles.some(uploaded => uploaded.id === file.id));  // Skip already uploaded files

            // Proceed with file upload if there are files to upload
            if (filteredSelectedFiles.length > 0) {
                const { storefileUploadResponse, storeFileNameArray } = await uploadFiles(filteredSelectedFiles);

                if (storefileUploadResponse?.length > 0) {
                    let storefileUploadResponseFinal = [];

                    for (let i = 0; i < storefileUploadResponse.length; i++) {
                        const file = storefileUploadResponse[i];
                        const documentTitle = storeFileNameArray.find((item) => item.id === file.fileName)?.filename;
                        const documenttype = documentTitle.split('.').pop().toLowerCase();

                        if (documentTitle) {
                            const payload = {
                                filename: file.fileName,
                                filesize: file.fileSize,
                                filepath: "D:/IFAC-X/UploadFolder",
                                documenttitle: documentTitle,
                                documenttype: documenttype,
                                mashospitaldetails: {
                                    id: hospitalDetailsId,
                                    hospitalname,
                                },
                            };

                            try {
                                const response = await saveFilesToAttachment(payload);
                                setHasUnsavedChanges(false);
                                if (response) {
                                    storefileUploadResponseFinal.push(response);
                                    fetchFiles();  // Reload files to update the UI
                                } else {
                                    console.log("Error saving file:", file.fileName);
                                }
                            } catch (error) {
                                console.log('Error saving file:', error);
                            }
                        } else {
                            console.log("Document Title not found for file:", file.fileName);
                        }
                    }

                    if (storefileUploadResponseFinal.length === storefileUploadResponse.length) {
                        setOpenDialog(true);

                        // Add successfully uploaded files to `previouslyUploadedFiles`
                        setPreviouslyUploadedFiles(prev => [...prev, ...filteredSelectedFiles]);
                    } else {
                        console.log("Error in uploading some files");
                    }
                } else {
                    console.log("No files registered");
                }
            } else {
                console.log("No files selected for upload");
            }

            // Handle deletions (remove files from the server)
            if (deletedRowIds.length > 0) {
                for (let id of deletedRowIds) {
                    try {
                        await axios.delete(`${MasterGraph}/api/mashospitalattachments/${id}`, config);
                    } catch (error) {
                        console.error(`Error deleting row with ID ${id}:`, error);
                    }
                }

                // Reset the deletedRowIds state after successful deletion
                setDeletedRowIds([]);
                setSuccessMessage('Data saved successfully.');
                setSuccessDialogOpen(true);
            }

            // Clear deleted files after saving
            if (deletedFilesArray.length > 0) {
                setDeletedFilesArray([]);  // Clear the deleted files array
            }

        } catch (error) {
            console.log('Error registering files:', error);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges && !isViewMode) {
            setCancelDialogOpen(true);
        } else {
            navigate(-1, { replace: true });
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };


    const handleSnackbarClose = () => {
        setError(false);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <TabContentLayout>
            <DefaultCard sx={{ height: '100%' }}>
                <form style={{ position: 'relative' }}>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <CustomTextField
                                    label="Hospital Code"
                                    id="hospital-code"
                                    value={hospitalcode}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <CustomTextField
                                    maxlength={225}
                                    label="Hospital Name"
                                    id="hospital-name"
                                    value={hospitalname}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Stack>
                        <Typography variant="labelSm" mt={0}>
                            Please add files in format of doc, jpg, png, pdf, xlsx, csv and
                            file size should be less than 20 MB.
                        </Typography>
                    </Stack>
                    {!isViewMode && (
                        <Stack direction={"row"} justifyContent={"end"}>
                            <Stack mb={1}>
                                <Button variant="contained" onClick={handleAddRow}>
                                    <AddRoundedIcon />
                                    Add File
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    <CustomAgGrid

                        rows={rows}
                        columns={[
                            {
                                field: 'file',
                                headerName: 'File',
                                flex: 1,
                                cellRenderer: (params) => (
                                    <Button 
                                        variant="outlined" 
                                        component="label" 
                                        disabled={params.data?.documenttitle}
                                        sx={{
                                            width: '100px',
                                            height: '38px',
                                            padding: '4px 8px',
                                            fontSize: '0.75rem',
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                        >
                                        {params.data.documenttitle ? 'FILE UPLOADED' : 'BROWSE FILE'}
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(event) => handleFileChange(event, params?.data?.id)}
                                        />
                                    </Button>
                                ),
                            },
                            {
                                field: 'documenttitle',
                                headerName: 'File Name',
                                flex: 1,
                            },
                            {
                                field: 'documenttype',
                                headerName: 'File Type',
                                flex: 1,
                            },
                            {
                                field: 'filesize',
                                headerName: 'Size',
                                flex: 1,
                            },
                            {
                                field: 'actions',
                                type: 'actions',
                                headerName: 'Actions',
                                flex: 1,
                                cellClassName: 'actions',
                                cellRenderer: (params) => (
                                    <>
                                        <Button onClick={handleDeleteClick(params?.data?.id)} disabled={isViewMode || ((rows?.length === 1 && params?.data?.id && !params?.data?.documenttitle) ? true : false)}>
                                            <DeleteIcon />
                                        </Button>
                                        <Button onClick={(event) => handleDownloadClick(event, params.data)}>
                                            {typeof (params?.data?.id) === 'string' ? <DownloadIcon /> : <></>}
                                        </Button>
                                    </>
                                ),
                            },
                        ]}
                        setGridApi={setGridApi}
                    />
                </form>
            </DefaultCard>
            {isViewMode ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box>

            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={handleSaveClick} disabled={isViewMode}>
                        Save
                    </Button>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box>
            )}

            <CustomDialog
                open={error}
                onClose={handleSnackbarClose}
                content={errorMessage}
                confirmBtn="OK"
                onConfirm={() => setError(false)}
            />
            <CustomDialog
                open={openDialog}
                onClose={handleCloseDialog}
                content="Files have been uploaded successfully."
                confirmBtn="OK"
                onConfirm={handleCloseDialog}
            />
            <CustomDialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                title="Unsaved Changes"
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleCancelDialogConfirm}
                onCancel={handleCancelDialogClose}
            />
            <CustomDialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                title="Confirm Delete"
                content="Are you sure you want to delete?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteDialogClose}
            />

            <CustomDialog
                open={successDialogOpen}
                onClose={handleSuccessDialogClose}
                content={successMessage}
                confirmBtn="OK"
                onConfirm={handleSuccessDialogClose}
            />

        </TabContentLayout >
    );
}
